<script>
import { useCookie } from "#imports";
import { useVoixToast } from "@voix/composables/useVoixToast";
import { useAdminStore } from "@voix/store/adminStore";
import { useLayoutStore } from "@voix/store/layoutStore";
import { usePageStore } from "@voix/store/pageStore";
import iframeResizer from "iframe-resizer";
import queryString from "query-string";
import { v4 as uuidv4 } from "uuid";
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  onMounted,
  ref,
  watch
} from "vue";
import VoixViewerBreakpointSelector from "./VoixViewerBreakpointSelector.vue";
export default defineComponent({
  components: { VoixViewerBreakpointSelector },
  props: {
    scale: {
      type: Number,
      required: true
    },
    keyString: {
      type: String,
      required: true
    }
  },
  emits: ["remove", "closeAndReopenIframe"],
  setup(props, { emit }) {
    const pageStore = usePageStore();
    const adminStore = useAdminStore();
    const theIframe = ref(null);
    const iframeResizerInstance = ref(null);
    const viewerWidth = ref(1200);
    const resizingStartX = ref(0);
    const isResizing = ref(false);
    const minWidth = 380;
    const maxWidth = 2400;
    const showGridOverlay = ref(false);
    const pageViewerResizeCookie = useCookie("voix-page-viewer-resize");
    const pageSlug = pageStore.currentPath.toString();
    const pagePath = computed(() => {
      if (pageSlug) {
        const parsedUrl = queryString.parseUrl(pageSlug);
        parsedUrl.query["voix-sync"] = adminStore.currentAdminId;
        parsedUrl.query["cache-buster"] = uuidv4();
        parsedUrl.query["show-grid"] = showGridOverlay.value ? "true" : "false";
        let url = parsedUrl.url;
        if (url[0] === "/")
          url = parsedUrl.url.slice(1);
        return `/${url}?${queryString.stringify(parsedUrl.query)}`;
      }
      return null;
    });
    const urlChecker = ref(null);
    onMounted(() => {
      window.addEventListener("mouseup", stopResize);
      window.addEventListener("mousemove", resize);
      if (pageViewerResizeCookie.value)
        viewerWidth.value = pageViewerResizeCookie.value;
    });
    onBeforeUnmount(() => {
      window.removeEventListener("mouseup", stopResize);
      window.removeEventListener("mousemove", resize);
      clearInterval(urlChecker.value);
    });
    function resizeToBreakpoint(breakpoint) {
      viewerWidth.value = breakpoint;
      pageViewerResizeCookie.value = viewerWidth.value;
    }
    function startResize(event) {
      isResizing.value = true;
      resizingStartX.value = event.clientX;
    }
    function stopResize() {
      isResizing.value = false;
      pageViewerResizeCookie.value = viewerWidth.value;
      if (iframeResizerInstance.value && iframeResizerInstance.value[0])
        iframeResizerInstance.value[0].iFrameResizer.resize();
    }
    function resize(event) {
      if (!isResizing.value)
        return;
      viewerWidth.value += event.clientX - resizingStartX.value;
      if (viewerWidth.value < minWidth)
        viewerWidth.value = minWidth;
      if (viewerWidth.value > maxWidth)
        viewerWidth.value = maxWidth;
      resizingStartX.value = event.clientX;
    }
    const setWidth = (event) => {
      const target = event.target;
      if (target) {
        if (Number.parseInt(target.value) > 380) {
          viewerWidth.value = Number.parseInt(target.value);
          target.blur();
        }
      }
    };
    function resetViewerWidth() {
      pageViewerResizeCookie.value = null;
      initIframeResizer();
    }
    function initIframeResizer() {
      iframeResizerInstance.value = iframeResizer.iframeResizer(
        {
          checkOrigin: false,
          scrolling: true,
          warningTimeout: 0
        },
        theIframe.value
      );
      viewerWidth.value = Math.floor(window.innerWidth - 48 * 2 - 360);
    }
    initIframeResizer();
    const scrollToElementComputed = computed(() => {
      if (adminStore.scrollToElement) {
        if (adminStore.scrollToElement?.properties?.dom_id)
          return adminStore.scrollToElement?.properties?.dom_id;
        return `voix-${adminStore.scrollToElement.type}-${adminStore.scrollToElement.id}`;
      }
      return null;
    });
    const voixToast = useVoixToast();
    watch(scrollToElementComputed, (element) => {
      if (element && theIframe.value && scrollToElementComputed.value) {
        const iframeElementToScrollTo = theIframe.value.contentDocument?.getElementById(
          scrollToElementComputed.value
        );
        if (iframeElementToScrollTo) {
          iframeElementToScrollTo.scrollIntoView();
          setTimeout(() => {
            adminStore.scrollToElement = null;
          }, 1e3);
        } else {
          voixToast.add("Could not find this slice. This is probably due to it the slice having multiple divs", "warning");
        }
      }
    });
    function iframeLoaded() {
      if (urlChecker.value)
        clearInterval(urlChecker.value);
      urlChecker.value = setInterval(() => {
        if (theIframe.value?.contentWindow?.location.pathname !== pageSlug) {
          console.info("Closing and reopening", theIframe.value?.contentWindow?.location.pathname, pageSlug);
          emit("closeAndReopenIframe");
        }
      }, 1e3);
    }
    const layoutStore = useLayoutStore();
    const layoutHasGrid = computed(() => {
      if (pageStore.currentPage?.layout) {
        const layout = pageStore.currentPage.layout;
        if (layoutStore.layouts[layout] && layoutStore.layouts[layout].grid)
          return true;
      }
      return false;
    });
    return {
      emit,
      props,
      theIframe,
      iframeLoaded,
      viewerWidth,
      isResizing,
      pageStore,
      pagePath,
      startResize,
      stopResize,
      resizeToBreakpoint,
      setWidth,
      resetViewerWidth,
      showGridOverlay,
      layoutHasGrid
    };
  }
});
</script>

<template>
  <div
    :style="{
      transform: `scale(${props.scale})`,
      width: `${viewerWidth * props.scale}px`,
    }"
  >
    <div class="flex justify-center items-start">
      <div class="relative flex flex-col justify-center items-center">
        <div class="absolute bottom-0 left-0 -mb-12 flex items-center">
          <input
            type="text" :value="viewerWidth"
            class="inline w-24 items-center px-3 pr-6 py-0.5 rounded-full text-sm font-medium voix-admin-bg-lightest voix-admin-text-dark border-0"
            @keyup.enter="setWidth"
          >
          <span class="absolute right-0 top-0 mr-2 mt-0.5 voix-admin-text-light text-sm font-medium">px</span>
        </div>
        <button
          class="absolute top-0 left-0 mb-5" :disabled="!layoutHasGrid" :title="layoutHasGrid ? 'Show Grid Overlay' : 'This layout has no grid configuration'" :class="{
            'opacity-30': !layoutHasGrid,
          }" @click="showGridOverlay = !showGridOverlay"
        >
          <Icon
            name="heroicons:view-columns" class="w-6 h-6 voix-admin-text" :class="{
              'opacity-50': !showGridOverlay,
            }"
          />
          <span class="block mt-1 text-[8px] uppercase voix-admin-text">
            Grid
          </span>
        </button>

        <div class="flex space-x-2">
          <VoixViewerBreakpointSelector
            class="voix-admin-text-light mb-4" :viewer-width="viewerWidth"
            @resize="resizeToBreakpoint"
          />
        </div>

        <div class="absolute top-0 right-0 mt-3 flex items-center">
          <button @click="$emit('remove')">
            <Icon name="heroicons:x-mark-20-solid" class="w-6 h-6 voix-admin-text" />
          </button>
        </div>

        <div class="relative" :style="{ width: `${viewerWidth}px` }">
          <div class="absolute z-0 inset-0 opacity-40 shadow-gray-500 shadow-2xl shadow-opacity-25" />

          <div v-if="pagePath" class="flex">
            <!-- Been 20 years of coding and iFrames are still in my life - Gary -->
            <iframe ref="theIframe" data-testid="voix-studio-iframe" :src="pagePath" width="100%" class="relative z-10 bg-white h-[80vh]" @load="iframeLoaded" />

            <div class="absolute z-10 inset-0" :class="{ 'pointer-events-none': !isResizing }" />

            <!-- Handle for resizing -->
            <div
              class="relative z-10 -mr-6 flex items-center justify-center w-6 bg-gray-200 border-l border-gray-300 rounded-r-xl cursor-ew-resize"
              @mousedown.stop.prevent="startResize"
              @mouseup.stop.prevent="stopResize"
              @dblclick.stop.prevent="resetViewerWidth"
            >
              <svg
                class="w-4 h-4 transform rotate-90 text-gray-400" fill="none" stroke="currentColor"
                viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M4 6h16M4 10h16M4 14h16M4 18h16"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
